import { useState, useEffect, useCallback, useRef } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  Expand,
  MapPin,
  Image as ImageIcon,
  Play,
  Hash
} from 'lucide-react';
import { Button } from '@/components/ui/Button/Button';
import { GalleryAsset } from '@/types/gallery';
import { isVideoUrl } from '../../utils/imageUrl';

interface ImageCarouselProps {
  assets: GalleryAsset[];
  initialIndex: number;
  onClose: () => void;
  galleryTitle: string;
  galleryLocation: {
    village: string;
    subdistrict: string;
    district: string;
  };
}

export const ImageCarousel: React.FC<ImageCarouselProps> = ({
  assets,
  initialIndex,
  onClose,
  galleryTitle,
  galleryLocation
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isLoading, setIsLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const currentAsset = assets[currentIndex];
  const currentIsVideo = isVideoUrl(currentAsset.cdn_url);

  // Navigation functions
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % assets.length);
    setIsLoading(true);
    setImageError(false);
  }, [assets.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + assets.length) % assets.length);
    setIsLoading(true);
    setImageError(false);
  }, [assets.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          event.preventDefault();
          goToNext();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [goToPrevious, goToNext, onClose]);

  // Touch/swipe support for mobile
  useEffect(() => {
    let startX = 0;
    let endX = 0;

    const handleTouchStart = (event: TouchEvent) => {
      startX = event.touches[0].clientX;
    };

    const handleTouchMove = (event: TouchEvent) => {
      endX = event.touches[0].clientX;
    };

    const handleTouchEnd = () => {
      const threshold = 50; // Minimum swipe distance
      const swipeDistance = startX - endX;

      if (Math.abs(swipeDistance) > threshold) {
        if (swipeDistance > 0) {
          // Swiped left - go to next
          goToNext();
        } else {
          // Swiped right - go to previous
          goToPrevious();
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [goToNext, goToPrevious]);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setImageError(true);
  };

  const handleDownload = () => {
    if (currentAsset.cdn_url) {
      const link = document.createElement('a');
      link.href = currentAsset.cdn_url;
      link.download = `${galleryTitle.replace(/\s+/g, '_')}_${currentAsset.sequence_number}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleViewOriginal = () => {
    if (currentAsset.cdn_url) {
      window.open(currentAsset.cdn_url, '_blank');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-black bg-opacity-50 text-white">
        <div className="flex items-center justify-between p-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold truncate">{galleryTitle}</h2>
            <p className="text-sm text-gray-300">
              {currentIndex + 1} of {assets.length}
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              className="text-white hover:bg-white hover:bg-opacity-20"
              icon={<Download className="h-4 w-4" />}
            >
              Download
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleViewOriginal}
              className="text-white hover:bg-white hover:bg-opacity-20"
              icon={currentIsVideo ? <Play className="h-4 w-4" /> : <Expand className="h-4 w-4" />}
            >
              {currentIsVideo ? 'Open Video' : 'Original'}
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20"
              icon={<X className="h-4 w-4" />}
            >
              Close
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 pt-20 pb-32">
        {/* Previous Button */}
        {assets.length > 1 && (
          <Button
            variant="ghost"
            size="lg"
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:bg-white hover:bg-opacity-20 z-10"
            icon={<ChevronLeft className="h-6 w-6" />}
          />
        )}

        {/* Media Container */}
        <div className="relative max-w-full max-h-full flex items-center justify-center">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {imageError ? (
            <div className="flex flex-col items-center justify-center text-white p-8">
              <ImageIcon className="h-16 w-16 mb-4 text-gray-400" />
              <p className="text-lg mb-2">Failed to load {currentIsVideo ? 'video' : 'image'}</p>
              <p className="text-sm text-gray-400">Asset ID: {currentAsset.asset_id}</p>
            </div>
          ) : currentIsVideo ? (
            <video
              ref={videoRef}
              key={currentAsset.id}
              src={currentAsset.cdn_url}
              controls
              autoPlay
              playsInline
              className="max-w-full max-h-full object-contain"
              onLoadedData={handleImageLoad}
              onError={handleImageError}
              style={{ display: isLoading ? 'none' : 'block', maxHeight: 'calc(100vh - 200px)' }}
            />
          ) : (
            <img
              key={currentAsset.id}
              src={currentAsset.cdn_url}
              alt={`Gallery image ${currentIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{ display: isLoading ? 'none' : 'block' }}
            />
          )}
        </div>

        {/* Next Button */}
        {assets.length > 1 && (
          <Button
            variant="ghost"
            size="lg"
            onClick={goToNext}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:bg-white hover:bg-opacity-20 z-10"
            icon={<ChevronRight className="h-6 w-6" />}
          />
        )}
      </div>

      {/* Footer with Metadata */}
      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0">
          {/* Asset Info */}
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center">
              <Hash className="h-4 w-4 mr-1" />
              <span>Sequence: {currentAsset.sequence_number}</span>
            </div>
            
            <div className="flex items-center">
              <ImageIcon className="h-4 w-4 mr-1" />
              <span>Asset ID: {currentAsset.asset_id}</span>
            </div>
            
          </div>

          {/* Location Info */}
          <div className="flex items-center text-sm">
            <MapPin className="h-4 w-4 mr-1" />
            <span>
              {galleryLocation.village}, {galleryLocation.subdistrict}, {galleryLocation.district}
            </span>
          </div>
        </div>

        {/* Thumbnail Navigation */}
        {assets.length > 1 && (
          <div className="mt-4 flex justify-center">
            <div className="flex space-x-2 overflow-x-auto max-w-full">
              {assets.map((asset, index) => (
                <button
                  key={asset.id}
                  onClick={() => {
                    if (videoRef.current) videoRef.current.pause();
                    setCurrentIndex(index);
                    setIsLoading(true);
                    setImageError(false);
                  }}
                  className={`relative flex-shrink-0 w-12 h-12 rounded overflow-hidden border-2 transition-all ${
                    index === currentIndex
                      ? 'border-rgf-primary-500 scale-110'
                      : 'border-transparent hover:border-white'
                  }`}
                >
                  {isVideoUrl(asset.cdn_url) ? (
                    <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                      <Play className="h-4 w-4 text-white" />
                    </div>
                  ) : (
                    <img
                      src={asset.cdn_url}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.style.display = 'none';
                      }}
                    />
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                  <div className="absolute inset-0 flex items-center justify-center text-white text-xs font-medium">
                    {asset.sequence_number}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};